import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '../utils/api';
import io from 'socket.io-client';
import Layout from '../components/Layout';
import MetaTags from '../components/MetaTags';
import { getSocketUrl } from '../utils/urlHelper';

const FlowGridView = () => {
  const { sessionId } = useParams();
  const [sessionInfo, setSessionInfo] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [displayQuestions, setDisplayQuestions] = useState([]); // Questions currently being displayed (for infinite scroll)
  const [flowGridSettings, setFlowGridSettings] = useState({
    sourceColumn: 'new',
    backgroundColor: '#1a1a1a',
    backgroundType: 'color', // 'color', 'gradient', 'image'
    backgroundImage: '',
    backgroundGradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    textColor: '#ffffff',
    frameBackground: '#2a2a2a',
    frameOpacity: 0.9,
    scrollingEnabled: true
  });
  const [loading, setLoading] = useState(true);
  const [socket, setSocket] = useState(null);
  const scrollContainerRef = useRef(null);
  const innerRef = useRef(null);
  const setHeightRef = useRef(0);         // height of ONE set (not both copies)
  const offsetRef = useRef(0);            // current translateY in px
  const rafRef = useRef(null);
  const lastTsRef = useRef(0);
  const SCROLL_PX_PER_SEC = 20;           // tweak speed here
  const [newQuestions, setNewQuestions] = useState(new Set()); // Track newly added questions for fade-in
  const lastQuestionsHashRef = useRef(''); // Track questions hash to prevent unnecessary updates

  // Initialize socket connection
  useEffect(() => {
    const socketUrl = getSocketUrl();
    const newSocket = io(socketUrl, {
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      timeout: 20000,
      forceNew: true,
      transports: ['websocket', 'polling']
    });
    
    newSocket.on('connect_error', (error) => {
      console.error('Flow Grid - Socket connection error:', error);
    });
    
    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
    };
  }, []);

  const sortQuestionsForDisplay = useCallback((list = []) => {
    return [...list].sort((a, b) => {
      const aHasOrder = typeof a.order_index === 'number';
      const bHasOrder = typeof b.order_index === 'number';

      if (aHasOrder || bHasOrder) {
        if (aHasOrder && !bHasOrder) return -1;
        if (!aHasOrder && bHasOrder) return 1;
        if (aHasOrder && bHasOrder && a.order_index !== b.order_index) {
          return a.order_index - b.order_index;
        }
      }

      const dateA = new Date(a.created_at || a.createdAt || 0).getTime();
      const dateB = new Date(b.created_at || b.createdAt || 0).getTime();
      return dateB - dateA;
    });
  }, []);

  // Function to fetch questions (can be called from socket listeners)
  const fetchQuestions = useCallback(async (sourceColumn = flowGridSettings.sourceColumn) => {
    try {
      const questionsResponse = await api.get(`/api/sessions/${sessionId}/questions`);
      const allQuestions = questionsResponse.data || [];
      
      // Filter questions based on source column
      const filteredQuestions = allQuestions.filter(q => {
        if (sourceColumn === 'new') return q.status === 'new';
        if (sourceColumn === 'selected') return q.status === 'selected';
        if (sourceColumn === 'on_air') return q.status === 'on_air';
        if (sourceColumn === 'done') return q.status === 'done';
        // For custom columns, check if the status matches custom_${columnId}
        return q.status === `custom_${sourceColumn}`;
      });

      const sortedQuestions = sortQuestionsForDisplay(filteredQuestions);
      setQuestions(sortedQuestions);
    } catch (error) {
      console.error('❌ Flow Grid - Error fetching questions:', error);
    }
  }, [sessionId, flowGridSettings.sourceColumn, sortQuestionsForDisplay]);

  // Join session room and listen for updates
  useEffect(() => {
    if (socket && sessionId) {
      socket.emit('join-room', sessionId);
      socket.emit('join-room', `session-${sessionId}`);

      // Listen for new questions
      socket.on('new-question', (newQuestion) => {
        if (newQuestion.session_id === sessionId) {
          setQuestions(prev => {
            // Check if this question matches our current source column
            const matchesSource = (() => {
              if (flowGridSettings.sourceColumn === 'new') return newQuestion.status === 'new';
              if (flowGridSettings.sourceColumn === 'selected') return newQuestion.status === 'selected';
              if (flowGridSettings.sourceColumn === 'on_air') return newQuestion.status === 'on_air';
              if (flowGridSettings.sourceColumn === 'done') return newQuestion.status === 'done';
              // For custom columns, check if the status matches custom_${columnId}
              return newQuestion.status === `custom_${flowGridSettings.sourceColumn}`;
            })();
            
            if (matchesSource && !prev.some(q => q.id === newQuestion.id)) {
              // Mark as new for fade-in animation
              setNewQuestions(prevNew => new Set([...prevNew, newQuestion.id]));
              
              // Remove from new questions after fade-in animation
              setTimeout(() => {
                setNewQuestions(prevNew => {
                  const updated = new Set(prevNew);
                  updated.delete(newQuestion.id);
                  return updated;
                });
              }, 2000); // Remove after 2 seconds
              
              // Add to the end of the list (no refetch needed)
              return [...prev, newQuestion];
            }
            return prev;
          });
        }
      });

      // Listen for question updates
      socket.on('question-updated', (updatedQuestion) => {
        if (updatedQuestion.session_id === sessionId) {
          setQuestions(prev => {
            // Check if this question matches our current source column
            const matchesSource = (() => {
              if (flowGridSettings.sourceColumn === 'new') return updatedQuestion.status === 'new';
              if (flowGridSettings.sourceColumn === 'selected') return updatedQuestion.status === 'selected';
              if (flowGridSettings.sourceColumn === 'on_air') return updatedQuestion.status === 'on_air';
              if (flowGridSettings.sourceColumn === 'done') return updatedQuestion.status === 'done';
              return updatedQuestion.custom_column_id && updatedQuestion.custom_column_id.toString() === flowGridSettings.sourceColumn;
            })();
            
            if (matchesSource) {
              // Update existing question or add if new
              const existingIndex = prev.findIndex(q => q.id === updatedQuestion.id);
              if (existingIndex >= 0) {
                const updated = [...prev];
                updated[existingIndex] = updatedQuestion;
                return updated;
              } else {
                return [...prev, updatedQuestion];
              }
            } else {
              // Remove question if it no longer matches our source column
              return prev.filter(q => q.id !== updatedQuestion.id);
            }
          });
        }
      });

      // Listen for question status updates (when dragged between columns)
      socket.on('question-status-updated', (data) => {
        if (data.sessionId === sessionId) {
          const { questionId, status } = data;
          
          // Check if this status change affects our current source column
          const affectsCurrentColumn = (() => {
            if (flowGridSettings.sourceColumn === 'new') return status === 'new';
            if (flowGridSettings.sourceColumn === 'selected') return status === 'selected';
            if (flowGridSettings.sourceColumn === 'on_air') return status === 'on_air';
            if (flowGridSettings.sourceColumn === 'done') return status === 'done';
            // For custom columns, check if the status matches our custom column ID
            return status === `custom_${flowGridSettings.sourceColumn}`;
          })();
          
          if (affectsCurrentColumn) {
            // Question was moved to our column, add it to the end
            setQuestions(prev => {
              if (!prev.some(q => q.id === questionId)) {
                const newQuestion = data.question;
                if (newQuestion) {
                  // Don't fade in moved questions - they should appear instantly
                  return [...prev, newQuestion];
                }
              }
              return prev;
            });
          } else {
            // Question was moved away from our column, remove it
            setQuestions(prev => prev.filter(q => q.id !== questionId));
          }
        }
      });

      // Listen for question reorders
      socket.on('questions-reordered', (data) => {
        const { status, questionIds, sessionId: eventSessionId, movedQuestionId } = data || {};

        if (String(eventSessionId) !== String(sessionId)) {
          return;
        }

        const matchesSource = (() => {
          if (flowGridSettings.sourceColumn === 'new') return status === 'new';
          if (flowGridSettings.sourceColumn === 'selected') return status === 'selected';
          if (flowGridSettings.sourceColumn === 'on_air') return status === 'on_air';
          if (flowGridSettings.sourceColumn === 'done') return status === 'done';
          return status === `custom_${flowGridSettings.sourceColumn}`;
        })();

        if (!matchesSource || !Array.isArray(questionIds) || questionIds.length === 0) {
          return;
        }

        setQuestions(prevQuestions => {
          if (!Array.isArray(prevQuestions) || prevQuestions.length === 0) {
            return prevQuestions;
          }

          const questionMap = new Map(prevQuestions.map(question => [question.id.toString(), question]));
          const reordered = [];

          questionIds.forEach(id => {
            const question = questionMap.get(id);
            if (question) {
              question.order_index = reordered.length;
              reordered.push(question);
            }
          });

          prevQuestions.forEach(question => {
            if (!questionIds.includes(question.id.toString())) {
              question.order_index = reordered.length;
              reordered.push(question);
            }
          });

          return reordered;
        });

        // Note: We don't trigger fade-in animation for reordered questions
        // Fade-in is only for truly new questions added to the system
      });

      // Listen for question deletions
      socket.on('question-deleted', (questionId) => {
        // Refetch questions to ensure we have the latest state
        fetchQuestions(flowGridSettings.sourceColumn);
      });

      // Listen for flow grid settings updates
      socket.on('flow-grid-settings-updated', (data) => {
        if (String(data.sessionId) === String(sessionId)) {
          setFlowGridSettings(data.settings);
          // Refetch questions with new settings
          setTimeout(() => {
            fetchQuestions(data.settings.sourceColumn);
          }, 100);
        }
      });

    }

    return () => {
      if (socket) {
        socket.off('new-question');
        socket.off('question-updated');
        socket.off('question-status-updated');
        socket.off('question-deleted');
        socket.off('flow-grid-settings-updated');
        socket.off('questions-reordered');
      }
    };
  }, [socket, sessionId, flowGridSettings.sourceColumn, fetchQuestions, sortQuestionsForDisplay]);

  // Fetch session info and initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch session info
        const sessionResponse = await api.get(`/api/sessions/${sessionId}`);
        const session = sessionResponse.data.session;
        setSessionInfo(session);

        // Fetch flow grid settings
        const settingsResponse = await api.get(`/api/sessions/${sessionId}/flow-grid-settings`);
        if (settingsResponse.data.settings) {
          setFlowGridSettings(settingsResponse.data.settings);
        }

        // Fetch initial questions
        await fetchQuestions();
        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setLoading(false);
      }
    };

    fetchData();
  }, [sessionId, flowGridSettings.sourceColumn, fetchQuestions]);

  // Initialize display questions when questions change
  useEffect(() => {
    if (!questions || questions.length === 0) {
      setDisplayQuestions([]);
      // Clear the hash when empty so next update will be detected
      lastQuestionsHashRef.current = '';
      return;
    }
    
    // Create a hash of question IDs and content to detect actual changes
    const questionsHash = questions.map(q => `${q.id}-${q.order_index}`).join(',');
    
    // Only update if questions actually changed
    if (questionsHash === lastQuestionsHashRef.current) {
      return;
    }
    
    lastQuestionsHashRef.current = questionsHash;
    
    // No reset of offsetRef here → preserves motion
    // Only duplicate if we have more than 15 questions (enough to fill viewport + need scrolling)
    if (questions.length > 15) {
      // Create 50 copies so reset happens much less frequently (every ~15 minutes at 20px/s)
      const manyCopies = [];
      for (let i = 0; i < 50; i++) {
        manyCopies.push(...questions);
      }
      setDisplayQuestions(manyCopies);
    } else {
      // 15 or fewer questions - just show them once, no scrolling needed
      setDisplayQuestions(questions);
    }
  }, [questions]);

  // Measure ONE set height after render (without resetting offset)
  useEffect(() => {
    if (!innerRef.current) return;

    // Wait a microtask so layout is ready
    const id = requestAnimationFrame(() => {
      const total = innerRef.current.scrollHeight;
      let oneSet;
      
      if (questions.length > 15) {
        // Total height is for 50 copies; divide by 50 to get one set
        oneSet = total / 50;
      } else {
        // No duplication, just use total height
        oneSet = total;
      }
      
      if (Number.isFinite(oneSet) && oneSet > 0) {
        setHeightRef.current = oneSet;
        // Ensure current offset stays within new bounds smoothly
        if (questions.length > 15) {
          offsetRef.current = offsetRef.current % oneSet;
        } else {
          offsetRef.current = 0; // No scrolling for 15 or fewer
        }
        innerRef.current.style.transform = `translateY(-${offsetRef.current}px)`;
      }
    });

    return () => cancelAnimationFrame(id);
  }, [displayQuestions, questions.length]);

  // rAF loop (no setInterval). Moves continuously if scrollingEnabled && enough items.
  useEffect(() => {
    const tick = (ts) => {
      if (!innerRef.current) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      if (flowGridSettings.scrollingEnabled && questions.length > 15) {
        const last = lastTsRef.current || ts;
        const dtSec = Math.min((ts - last) / 1000, 0.05); // cap to avoid big jumps
        lastTsRef.current = ts;

        // Advance offset
        const next = offsetRef.current + SCROLL_PX_PER_SEC * dtSec;

        // Loop seamlessly using modulo of one-set height
        const oneSet = setHeightRef.current || 0;
        if (oneSet > 0) {
          offsetRef.current = next % oneSet;
        } else {
          offsetRef.current = next; // until we have a measurement
        }

        innerRef.current.style.transform = `translateY(-${offsetRef.current}px)`;
        innerRef.current.style.willChange = 'transform';
      } else {
        // Paused: just reset the timestamp so we don't get a big jump when resuming
        // Keep offsetRef.current as-is (don't reset to 0)
        lastTsRef.current = ts;
        
        // If we have 15 or fewer questions, reset to top (no scrolling)
        if (questions.length <= 15) {
          offsetRef.current = 0;
          if (innerRef.current) {
            innerRef.current.style.transform = 'translateY(0px)';
          }
        }
        // Otherwise, keep current position (pause in place)
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [flowGridSettings.scrollingEnabled, questions.length]);

  // Periodic sync to ensure Flow Grid stays in sync (fallback for missed socket events)
  useEffect(() => {
    if (!sessionId || !flowGridSettings.sourceColumn) return;

    const syncInterval = setInterval(() => {
      fetchQuestions(flowGridSettings.sourceColumn);
    }, 10000); // Sync every 10 seconds

    return () => clearInterval(syncInterval);
  }, [sessionId, flowGridSettings.sourceColumn, fetchQuestions]);

  // Refetch questions when source column changes
  useEffect(() => {
    if (sessionId && flowGridSettings.sourceColumn) {
      fetchQuestions(flowGridSettings.sourceColumn);
    }
  }, [flowGridSettings.sourceColumn, sessionId, fetchQuestions]);


  const getBackgroundStyle = () => {
    switch (flowGridSettings.backgroundType) {
      case 'gradient':
        return {
          background: flowGridSettings.backgroundGradient
        };
      case 'image':
        return {
          backgroundImage: `url(${flowGridSettings.backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        };
      default:
        return {
          backgroundColor: flowGridSettings.backgroundColor
        };
    }
  };

  // Calculate dynamic font size based on question length
  const getDynamicFontSize = (contentLength) => {
    // Base font size from settings or default
    const baseSizeMap = {
      'text-xs': 12,
      'text-sm': 14,
      'text-base': 16,
      'text-lg': 18,
      'text-xl': 20,
      'text-2xl': 24,
      'text-3xl': 30,
      'text-4xl': 36
    };
    
    const baseSize = baseSizeMap[flowGridSettings.fontSize] || 20; // default to text-xl (20px)
    
    // Scale down based on length
    if (contentLength <= 100) {
      return `${baseSize}px`;
    } else if (contentLength <= 150) {
      return `${baseSize * 0.9}px`;
    } else if (contentLength <= 200) {
      return `${baseSize * 0.8}px`;
    } else {
      // 200-260 characters
      return `${baseSize * 0.7}px`;
    }
  };


  if (loading) {
    return (
      <Layout title="Loading Flow Grid..." showHeader={false} fullWidth={true}>
        <div className="min-h-screen flex items-center justify-center" style={getBackgroundStyle()}>
          <div className="text-white text-xl">Loading flow grid...</div>
        </div>
      </Layout>
    );
  }

  if (!sessionInfo) {
    return (
      <Layout title="Session Not Found" showHeader={false} fullWidth={true}>
        <div className="min-h-screen flex items-center justify-center" style={getBackgroundStyle()}>
          <div className="text-white text-xl">Session not found</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout 
      title={`Flow Grid - ${sessionInfo.name || 'Live Session'}`} 
      showHeader={false}
      showFooter={false}
      fullWidth={true}
    >
      <MetaTags 
        title={`Flow Grid - ${sessionInfo.name || 'Live Session'} - AskTide`}
        description="Live question flow display for broadcast integration"
        url={window.location.href}
      />
      
      <div 
        className="min-h-screen overflow-hidden"
        style={getBackgroundStyle()}
      >
        <div 
          ref={scrollContainerRef}
          className="h-screen overflow-hidden"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {displayQuestions.length > 0 && (
            <div 
              ref={innerRef}
              className="p-4 md:p-8"
              // No transform/transition here; the rAF loop sets style.transform directly
            >
              {/* Single grid container that wraps automatically based on responsive columns */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {displayQuestions.map((question, index) => (
                  <div
                    key={`${question.id}-${index}`}
                    className={`flex flex-col justify-center items-center text-center transition-all duration-1000 ${
                      newQuestions.has(question.id) ? 'fade-in-new' : 'fade-in'
                    }`}
                    style={{
                      backgroundColor: `${flowGridSettings.frameBackground}${Math.round(flowGridSettings.frameOpacity * 255).toString(16).padStart(2, '0')}`,
                      padding: '20px',
                      borderRadius: '12px',
                      boxShadow: flowGridSettings.showShadow ? '0 4px 12px rgba(0, 0, 0, 0.3)' : 'none',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      minHeight: '200px'
                    }}
                  >
                    <div 
                      className="font-medium mb-2 break-words whitespace-pre-wrap overflow-hidden"
                      style={{ 
                        color: flowGridSettings.textColor,
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        hyphens: 'auto',
                        fontSize: getDynamicFontSize(question.content?.length || 0),
                        lineHeight: '1.4'
                      }}
                    >
                      {question.content}
                    </div>
                    {question.submitterName?.trim() && question.submitterName.trim().toLowerCase() !== 'anonymous' && (
                      <div 
                        className="text-sm opacity-80"
                        style={{ color: flowGridSettings.textColor }}
                      >
                        — {question.submitterName}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .fade-in {
          animation: fadeIn 0.8s ease-in-out;
        }
        
        .fade-in-new {
          animation: fadeInNew 2s ease-in-out;
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fadeInNew {
          0% {
            opacity: 0;
            transform: translateY(30px) scale(0.95);
          }
          50% {
            opacity: 0.7;
            transform: translateY(15px) scale(0.98);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </Layout>
  );
};

export default FlowGridView;
